/**
 * StudentLiveScreen — le live de l'élève (la route `menu` sous le titre `live`) : `<StudentLivePage>`
 * en deux temps (Dialogue : le masteur devant ; Concentré : Sam devant, le masteur derrière lui), l'activité
 * composée (`<StudentActivityBody>`), et autour, ce qui ne se voit pas : l'appel (CallKeep, le micro
 * qui suit le mode, le son de la salle).
 *
 * Un fil : l'état (`useStudentLiveScreen`), les pistes dans les fentes de la scène (`LiveVideo`),
 * les feuilles (`StudentLiveLayers`). Une activité faite qu'on relit prend la place de l'activité dans
 * le volet (`StudentFinishedActivity`), avec son retour.
 *
 * La barre d'état suit le thème, comme dans la coquille (`ParentStatusBar`) : la route du live
 * remplace la coquille à onglets, qui la posait, et sur Android la bande restait blanche, icônes
 * sombres, au-dessus du live en Sombre.
 */
import moment from "moment"
import React, { useCallback, useEffect, useState } from "react"
import { AppState } from "react-native"

import { LiveAppBanner, StudentLivePage } from "../../../../../../design-system/student"
import CallKeepManagerM from "../../../../../../engine/live/room/callKeep/CallKeepManagerM"
import { MuteCurrentUser } from "../../../../../../engine/live/room/MuteCurrentUser"
import { usePermissionRequestPending } from "../../../../../../engine/precall/permissionPrompt"
import { useLiveActivityEnlarged } from "../../../hooks/liveActivityWidthPreference"
import {
    setStudentAppearancePreference,
    useStudentAppearancePreference,
} from "../../../hooks/studentAppearancePreference"
import { useStudentThemeScope } from "../../../hooks/useStudentThemeScope"
import { type UILiveSamLine, type UILiveVideoFit, type UILiveVideoSource } from "../../../types/studentLive"
import { ParentStatusBar } from "../../parent/ParentStatusBar"
import { LiveVoiceSlot } from "../../shared/live/LiveVoiceSlot"
import { type ApiFinishedActivity } from "../../../../adapters/student/Activity/finishedActivityAdapter"
import { liveMicSurface, liveSamLine } from "../../../../view-models/student/Live/studentLiveViewModel"
import { StudentActivityBody } from "../Activity/StudentActivityBody"
import { StudentFinishedActivity } from "../Activity/StudentFinishedActivity"
import { type StudentSpaceScreenProps } from "../studentSpaceRouteTypes"
import { LiveAudio } from "./LiveAudio"
import { LiveMicDialog } from "./LiveMicDialog"
import { LivePermissionCoach } from "./LivePermissionCoach"
import { LiveVideo } from "./LiveVideo"
import { StudentLiveLayers } from "./StudentLiveLayers"
import { LIVE_EVENTS, trackLive } from "./liveTracking"
import { useStudentLiveScreen } from "./useStudentLiveScreen"

export function StudentLiveScreen(props: StudentSpaceScreenProps) {
    void props
    // Le live sort de la coquille de l'espace élève (la pile bascule) : il reprend son thème, Automatique par défaut.
    useStudentThemeScope()
    // Le thème se change aussi pendant le live (le menu ⋮) : la même préférence que « Mon compte ».
    const appearance = useStudentAppearancePreference()
    // Au grand écran, l'activité agrandie à côté de la visio : un choix de l'appareil, gardé d'un live à l'autre.
    const activityEnlarge = useLiveActivityEnlarged()
    const m = useStudentLiveScreen()
    const { live } = m
    const { actions, state } = live
    // Les phrases de Sam refermées par leur croix : elles ne reviennent pas.
    const [closedLines, setClosedLines] = useState<ReadonlySet<UILiveSamLine>>(() => new Set())
    const notices = StudentLiveLayers.useNotices(m)

    const renderVideo = useCallback(
        (source: UILiveVideoSource, fit?: UILiveVideoFit) => {
            const track = m.tracks[source]
            return track != null ? <LiveVideo track={track} source={source} fit={fit} /> : null
        },
        [m.tracks]
    )
    const onStageMorphStuck = () =>
        live.telemetry.error("stage_morph_stuck", new Error("stage morph not finished"), {
            appState: AppState.currentState,
        })
    const session = m.heading ?? {
        date: moment().format("YYYY-MM-DDTHH:mm:ss"),
        duration: 60,
        tutor: m.tutorUI,
        subjects: [],
    }
    // La bulle de Sam : pendant l'attente du tuteur, sans activité encore, ou au passage en Concentré d'un
    // cours partagé (`liveSamLine`). Jamais en Dialogue : Sam n'y est pas.
    const samLine = liveSamLine({
        dialogue: live.dialogue,
        waiting: m.waiting != null,
        tutorPresent: !m.tutorAbsent,
        hasActivity: m.activity != null || m.review != null,
        finishedSome: m.finishedSome,
        focusedJustNow: state.samBubble,
        sharedCourse: m.sharedCourse,
        closed: closedLines,
    })
    // Sam a dit que le masteur est là, sans activité : le passage en Concentré qu'elle a couvert est dit aussi,
    // il ne ressort pas, périmé, quand l'activité arrive.
    const dismissSamBubble = actions.dismissSamBubble
    useEffect(() => {
        if (samLine === "first_activity" && state.samBubble) dismissSamBubble()
    }, [samLine, state.samBubble, dismissSamBubble])

    return (
        <>
            <CallKeepManagerM />
            {/* Le micro ne suit le mode qu'une fois libre : tenu, seul un geste de l'élève l'ouvre (`micGate`). */}
            {live.room.state.connection === "connected" && !live.micGate.held && (
                <MuteCurrentUser isSilentMode={!live.dialogue} />
            )}
            <LiveMicLayer live={live} tutorFirstname={m.tutorUI.firstname} />
            <LiveVoiceSlot>
                <StudentLivePage
                    session={session}
                    soundMode={m.soundMode}
                    view={state.view}
                    onChangeView={actions.changeView}
                    onTabsChange={actions.changeLayout}
                    tutorTalking={state.tutorTalking}
                    onDismissTutorTalking={actions.dismissTutorTalking}
                    activityAdded={m.activityAdded}
                    onShowAddedActivity={m.sheets.showAddedActivity}
                    onDismissAddedActivity={actions.dismissAddedActivity}
                    onActivityOnScreen={m.onActivityOnScreen}
                    onStageMorphStuck={onStageMorphStuck}
                    activityKey={m.activityId}
                    activities={{ count: m.todos.length, onPress: () => m.sheets.setActivitiesOpen(true) }}
                    stage={{
                        tutor: m.tutor,
                        tutorAbsent: m.tutorAbsent,
                        tutorRounds: m.tutorRounds,
                        me: m.me,
                        twins: m.twins,
                        onFlipCamera: m.cameraFlip?.flip,
                        renderVideo,
                        connection: live.connection,
                        onCancelConnection: live.leave.leave,
                        onRetryConnection: actions.retryConnection,
                    }}
                    poorConnection={live.poorConnection}
                    waiting={m.waiting}
                    activityEnlarge={activityEnlarge}
                    renderActivity={
                        m.review != null
                            ? () => (
                                  <StudentFinishedActivity
                                      key={String(m.review?.id)}
                                      activity={m.review as ApiFinishedActivity}
                                      inline
                                      openWork={m.reviewWorkOpen}
                                      onBack={m.sheets.closeReview}
                                      onReplayed={m.openReplayed}
                                  />
                              )
                            : m.activity != null
                              ? ({ tandem, onFooterHeight, column, onScreen }) => (
                                    <StudentActivityBody
                                        view={m.activityView}
                                        onFinished={m.onActivityFinished}
                                        tandem={tandem}
                                        onFooterHeight={onFooterHeight}
                                        column={column}
                                        onScreen={onScreen}
                                        countdownHeld={m.sheets.activitiesOpen}
                                    />
                                )
                              : undefined
                    }
                    noActivityYet={!m.finishedSome}
                    newActivity={state.newActivity}
                    onDismissNewActivity={actions.dismissNewActivity}
                    sam={
                        live.checkpointsEnabled
                            ? {
                                  onOpen: m.sheets.openSam,
                                  line: samLine,
                                  onDismissBubble: () => {
                                      // Le passage en Concentré se dit au moteur ; les autres phrases, ici.
                                      if (samLine === "shared_focus") dismissSamBubble()
                                      else if (samLine != null) setClosedLines((closed) => new Set(closed).add(samLine))
                                  },
                                  sent: state.samSent,
                                  sentNotice: state.samSentNotice,
                                  onDismissSentNotice: actions.dismissSamSentNotice,
                              }
                            : undefined
                    }
                    controls={{
                        micOn: live.micOn,
                        onToggleMic: actions.toggleMic,
                        cameraOn: live.cameraOn,
                        onToggleCamera: actions.toggleCamera,
                        screenShare: m.screenShare ?? undefined,
                        onOpenMicDevices:
                            m.devices != null
                                ? () => m.devicesDispatch({ type: "OPEN", from: "microphone" })
                                : undefined,
                        onOpenCameraDevices:
                            m.devices != null ? () => m.devicesDispatch({ type: "OPEN", from: "camera" }) : undefined,
                        handRaised: state.handRaised.value,
                        onToggleHand: actions.toggleHand,
                        unreadCount: m.unreadCount,
                        onOpenChat: () => live.chat.setOpen(true),
                        problemDetected: m.problemDetected,
                        micBlocked: m.micBlocked,
                        cameraBlocked: m.cameraBlocked,
                        onOpenHelp: m.sheets.openHelp,
                        appearance: { value: appearance, onChange: setStudentAppearancePreference },
                        onLeave: m.sheets.askLeave,
                        leaving: live.leave.leaving,
                    }}
                    micHint={state.micHint}
                    onDismissMicHint={actions.dismissMicHint}
                    confirmLeave={m.sheets.confirmLeave}
                    onConfirmLeave={() => {
                        trackLive(LIVE_EVENTS.leaveConfirmed)
                        m.sheets.confirmLeaving()
                    }}
                    onCancelLeave={() => {
                        trackLive(LIVE_EVENTS.leaveCancelled)
                        m.sheets.cancelLeave()
                    }}
                    onDismissLeave={m.sheets.cancelLeave}
                    leaveSheet={live.leave.rating.open || live.leave.leaving}
                    connectedStudents={m.connectedStudents}
                    chatLayer={StudentLiveLayers.chat(m)}
                    checkpointLayer={StudentLiveLayers.checkpoint(m)}
                    checkpointAboveControls
                    notices={notices}
                    appBanner={m.appBanner != null ? <LiveAppBanner {...m.appBanner} /> : undefined}
                    notice={live.notice}
                    noticeMessage={live.noticeMessage}
                    onCloseNotice={actions.closeNotice}
                    onKeepTab={actions.keepTab}
                />
            </LiveVoiceSlot>
            <StudentLiveLayers model={m} />
            <ParentStatusBar />
        </>
    )
}

/**
 * Le micro et le son de la salle, à l'écran : le guidage de la question du navigateur, la boîte du micro,
 * celle du son (`liveMicSurface`). Seul calque à suivre la demande en cours (`usePermissionRequestPending`) :
 * le live ne se redessine pas à chaque demande du micro.
 */
function LiveMicLayer(props: {
    live: ReturnType<typeof useStudentLiveScreen>["live"]
    tutorFirstname: string | null | undefined
}) {
    const { live, tutorFirstname } = props
    const surface = liveMicSurface(live.micGate, live.micRefusal, usePermissionRequestPending())
    return (
        <>
            <LiveAudio dialogue={live.dialogue} covered={surface.covered} telemetry={live.telemetry} />
            <LivePermissionCoach tutorFirstname={tutorFirstname} />
            <LiveMicDialog
                dialog={surface.dialog}
                refusal={live.micRefusal}
                tutorFirstname={tutorFirstname}
                onActivate={live.actions.activateMic}
                onDismissAsk={live.actions.dismissMicAsk}
                onRetry={live.actions.retryMic}
                onDismiss={live.actions.dismissMicRefusal}
                telemetry={live.telemetry}
            />
        </>
    )
}
